import type { CSSProperties } from 'react';

export type ReviewSize = 'sm' | 'md' | 'lg';
export type ReviewAlign = 'start' | 'center' | 'end';
export type ReviewFieldKey = 'rating' | 'review' | 'reviewerIcon' | 'reviewerName';

export interface ReviewDisplaySettings {
  fields?: Partial<Record<ReviewFieldKey, boolean>>;
  fieldOrder?: ReviewFieldKey[];
  gap?: ReviewSize;
  padding?: ReviewSize | 'none';
  align?: ReviewAlign;
  borderColor?: string;
  backgroundColor?: string;
}

export function reviewFieldEnabled(
  settings: ReviewDisplaySettings | undefined,
  key: ReviewFieldKey,
): boolean {
  return settings?.fields?.[key] !== false;
}

const REVIEW_FIELD_KEYS: ReviewFieldKey[] = ['rating', 'review', 'reviewerIcon', 'reviewerName'];

const GRID_FIELD_ORDER: ReviewFieldKey[] = ['reviewerIcon', 'reviewerName', 'rating', 'review'];

export function resolveReviewFieldOrder(
  settings: ReviewDisplaySettings | undefined,
  variant: string | undefined,
): ReviewFieldKey[] {
  const stored = (settings?.fieldOrder ?? []).filter(
    (key, index, keys) => REVIEW_FIELD_KEYS.includes(key) && keys.indexOf(key) === index,
  );
  const defaults = variant === 'list' || variant === 'cards' ? REVIEW_FIELD_KEYS : GRID_FIELD_ORDER;
  return [...stored, ...defaults.filter((key) => !stored.includes(key))];
}

export type ReviewCardSegment = 'rating' | 'review' | 'attribution';

export function resolveReviewCardSegments(
  settings: ReviewDisplaySettings | undefined,
  variant: string | undefined,
): ReviewCardSegment[] {
  const segments: ReviewCardSegment[] = [];
  for (const key of resolveReviewFieldOrder(settings, variant)) {
    const segment = key === 'reviewerIcon' || key === 'reviewerName' ? 'attribution' : key;
    if (!segments.includes(segment)) segments.push(segment);
  }
  return segments;
}

export interface ReviewSegments {
  showIcon: boolean;
  showName: boolean;
  segments: ReviewCardSegment[];
}

export function resolveReviewSegments(
  settings: ReviewDisplaySettings | undefined,
  variant: string | undefined,
): ReviewSegments {
  const showRating = reviewFieldEnabled(settings, 'rating');
  const showReview = reviewFieldEnabled(settings, 'review');
  const showIcon = reviewFieldEnabled(settings, 'reviewerIcon');
  const showName = reviewFieldEnabled(settings, 'reviewerName');

  return {
    showIcon,
    showName,
    segments: resolveReviewCardSegments(settings, variant).filter((segment) =>
      segment === 'rating' ? showRating : segment === 'review' ? showReview : showIcon || showName,
    ),
  };
}

export const REVIEW_ALIGN_ITEMS_CLASS: Record<ReviewAlign, string> = {
  start: 'items-start text-left',
  center: 'items-center text-center',
  end: 'items-end text-right',
};

export function resolveReviewSizeClass(
  value: unknown,
  classes: Record<ReviewSize, string>,
  fallback: ReviewSize,
): string {
  if (value === 'none') return '';

  const key: ReviewSize = value === 'sm' || value === 'md' || value === 'lg' ? value : fallback;
  return classes[key];
}

export function reviewCardStyle(settings: ReviewDisplaySettings | undefined): CSSProperties {
  const style: CSSProperties = {};
  if (settings?.borderColor) style.borderColor = settings.borderColor;
  if (settings?.backgroundColor) style.backgroundColor = settings.backgroundColor;
  return style;
}
